import { ImagePaths } from '../../lib/imageUrls.js';

export const aiForBusiness = {

    id: 11,
    slug: 'ai-for-business',
    ogImage: '/og/courses/ai-for-business.jpg',
    courseCode: 'AI-BIZ-001',
    title: 'AI Catalyst: Productivity & Policy for Business',
    subtitle: 'Harness AI for business productivity within responsible policies',
    valueProposition: 'Empower non-technical staff and SMBs to leverage approved AI tools productively, automate workflows, and operate within robust data privacy policies aligned to Australian compliance frameworks.',
    duration: '24 hours',
    students: 'New',
    level: 'Foundational',
    image: ImagePaths.COURSE_1,
    shortDescription: 'AI-BIZ-001 — Business AI training covering prompt engineering, workflow automation, and policy compliance for managers and SMB owners.',
    // Repriced $399 -> $899 (#326). At $399 for 24 hours this sat BELOW the
    // four-hour AI Quick Start courses at $395 — $16.63/hr against $98.75/hr —
    // so the "rung below" positioning on ai-quick-start-small-business read as
    // nonsense to anyone who compared the two.
    //
    // earlyBird removed rather than rescaled: $349 was set against $399 and
    // would have implied a 61% discount off $899. A replacement early-bird price
    // is a commercial decision, not a mechanical conversion.
    price: {
      aud: 899,
      currency: 'AUD'
    },
    // price_tbd forces stripe:sync to mint a new Price. Stripe Prices are
    // immutable, so the old price_1T9bE3... is archived separately.
    stripeConfig: {
    priceId: 'price_1U3ppdKu1nyc322UQOuIHZ9u',
    productId: 'prod_V3xlmLzvZZQYS8'
  },
    
    snapshot: {
      whoItsFor: [
        'Business managers and team leaders',
        'Corporate staff (teaching & administrative professionals)',
        'SMB owners and entrepreneurs',
        'Non-technical professionals adopting AI in workflows',
        'Organisations developing AI policies and governance'
      ],
      accreditation: 'Aligned to Australian Privacy Principles, Essential Eight, and ISO 27001 data handling standards'
    },
    
    logistics: {
      classSize: 'Maximum 20 participants for hands-on practice',
      requirements: [
        'Laptop with Windows 10+ or macOS',
        'Reliable internet connection (minimum 10 Mbps)',
        'Access to approved AI tools (guidance provided)',
        'No technical or programming experience required'
      ]
    },
    
    overview: 'This practical, business-focused course equips non-technical professionals with the skills to harness generative AI safely and productively within formal organisational policies. Designed for corporate staff, managers, and SMB owners, the course covers real-world prompt engineering, workflow automation, task enhancement, and critical data privacy awareness. Grounded in Australian compliance frameworks including the Australian Privacy Principles (APPs), Essential Eight, and ISO 27001, participants learn to operate AI tools responsibly while protecting sensitive data and meeting organisational governance requirements.',
    
    outcomes: [
      'Apply practical prompt engineering techniques to enhance daily work tasks and workflows',
      'Automate routine business processes using approved AI tools while maintaining quality control',
      'Navigate and implement organisational AI policies that balance productivity with data protection',
      'Identify and mitigate data privacy risks when using AI tools with customer or employee information',
      'Evaluate AI-generated outputs for accuracy, bias, and business appropriateness',
      'Develop AI use cases aligned to business objectives and compliance requirements',
      'Communicate AI capabilities and limitations effectively to stakeholders and team members',
      'Build responsible AI adoption practices compliant with Australian Privacy Principles and Essential Eight'
    ],
    
    audience: [
      'Business managers and team leaders',
      'Corporate staff in education, healthcare, or professional services',
      'Teaching and administrative staff at educational institutions',
      'SMB owners and entrepreneurs seeking productivity gains',
      'Marketing, HR, and operations professionals',
      'Non-technical staff tasked with AI adoption'
    ],
    
    prerequisites: [
      'Basic computer literacy and familiarity with business software',
      'Understanding of your organisation\'s workflows and processes',
      'No technical, programming, or AI experience required',
      'Willingness to explore new tools and adapt workflows'
    ],
    
    modules: [
      {
        title: 'Introduction to Business AI',
        duration: '3 hours',
        topics: [
          'Generative AI landscape for business applications',
          'Approved AI tools for enterprise and SMBs',
          'Understanding AI capabilities and limitations',
          'AI use cases across departments (marketing, HR, operations)',
          'Cost-benefit analysis and ROI considerations'
        ]
      },
      {
        title: 'Practical Prompt Engineering for Business',
        duration: '8 hours',
        topics: [
          'Fundamentals of effective prompt design',
          'Prompt patterns for common business tasks (emails, reports, analysis)',
          'Iterative refinement and context management',
          'Handling ambiguity and improving AI responses',
          'Sector-specific prompting strategies (education, corporate, SMBs)',
          'Hands-on exercises with real business scenarios'
        ]
      },
      {
        title: 'Workflow Automation and Productivity',
        duration: '5 hours',
        topics: [
          'Identifying automation opportunities in daily workflows',
          'Using AI for content creation and editing',
          'Automating research, summarisation, and data analysis',
          'Integrating AI into existing business tools and platforms',
          'Quality assurance and human oversight of AI outputs',
          'Measuring productivity gains and efficiency improvements'
        ]
      },
      {
        title: 'Data Privacy and Security Awareness',
        duration: '4 hours',
        topics: [
          'Australian Privacy Principles (APPs) and AI data handling',
          'Protecting customer, employee, and organisational data',
          'Understanding data residency and third-party AI providers',
          'Identifying sensitive information and redaction strategies',
          'Essential Eight principles for AI security',
          'Privacy impact considerations for AI deployments'
        ]
      },
      {
        title: 'AI Policy and Governance',
        duration: '3 hours',
        topics: [
          'Developing organisational AI acceptable use policies',
          'Balancing innovation with risk management and compliance',
          'Roles, responsibilities, and accountability in AI adoption',
          'Incident response for AI misuse or data breaches',
          'Aligning AI use to ISO 27001 and industry standards',
          'Communicating policies across the organisation'
        ]
      },
      {
        title: 'Implementing Responsible AI Practices',
        duration: '1 hour',
        topics: [
          'Building a culture of responsible AI use',
          'Monitoring and auditing AI tool usage',
          'Continuous learning and staying current with AI developments',
          'Next steps: scaling AI adoption in your organisation'
        ]
      }
    ],
    
    format: {
      type: 'Live online training',
      includes: [
        '24 hours of interactive instruction',
        'Hands-on prompt engineering workshops',
        'Real business case studies and scenarios',
        'AI policy templates and implementation guides',
        'Access to approved AI tool trial accounts',
        'Recorded sessions for 6 months',
        'Certificate of completion',
        'Post-course support and resource library',
        'Monthly follow-up Q&A sessions for 3 months'
      ]
    },
    
    nextIntake: {
      date: 'Register your interest — we will confirm the next cohort',
      details: 'Eight-week program (3 hours per week) with flexible sessions for working professionals'
    },
    
    // Pathway relationships
    pathways: [
      'ai-foundations-professional',
      'ai-security-pathway'
    ],

    skillsTaught: [
      'AI workflow automation',
      'Prompt engineering for business',
      'AI governance and policy',
      'Data privacy & Australian compliance',
      'AI productivity strategies'
    ],

    hasCapstone: false,

    nextCourses: [
      'ai-200-azure-ai-developer-associate',
      'ai-governance-risk-compliance'
    ],
    
    faqs: [
      {
        question: 'Is this course technical? Do I need programming skills?',
        answer: 'No technical or programming skills required. This course is designed specifically for non-technical professionals. We focus on practical use of AI tools through prompting and workflow integration—no coding involved.'
      },
      {
        question: 'Which AI tools will we use in the course?',
        answer: 'We teach principles applicable to any generative AI tool (ChatGPT, Claude, Copilot, Gemini, etc.). You\'ll practise with widely available approved tools, and we provide guidance on evaluating tools for your specific organisational context and policies.'
      },
      {
        question: 'How does this course address Australian privacy and compliance requirements?',
        answer: 'The course is grounded in Australian compliance frameworks including the Australian Privacy Principles (APPs), Privacy Act requirements, Essential Eight security controls, and ISO 27001 data handling standards. All examples, policies, and practices reflect Australian regulatory context.'
      },
      {
        question: 'Can we customise this course for our organisation\'s specific needs?',
        answer: 'Yes! We offer tailored in-house delivery for organisations wanting to align the course with your specific AI tools, workflows, policies, and industry requirements. This is ideal for teaching staff, corporate teams, or SMB groups. Contact us for customisation options.'
      },
      {
        question: 'What if my organisation doesn\'t have an AI policy yet?',
        answer: 'Perfect—this course will help you develop one. We provide AI policy templates, governance frameworks, and practical implementation guidance. You\'ll leave equipped to propose or shape your organisation\'s AI adoption strategy.'
      },
      {
        question: 'Will I receive proof of completion for professional development?',
        answer: 'Yes, you\'ll receive a Certificate of Completion that can be added to your LinkedIn profile and used for professional development records. The course qualifies for continuing education hours in many professional contexts.'
      },
      {
        question: 'How much time should I allocate outside of live sessions?',
        answer: 'We recommend 2-3 hours per week for practice exercises, experimenting with prompts, and applying concepts to your work. The course is designed to integrate directly into your workflows, so practice time is productive work time.'
      },
      {
        question: 'What\'s the difference between this course and AI for Families?',
        answer: 'AI for Families focuses on safety, duty of care, and readiness for parents and students. AI Catalyst is a practical productivity course for business professionals, emphasising hands-on prompt engineering, workflow automation, and formal policy compliance in corporate/SMB environments.'
      }
    ]
};
